/**
 * Inventory Lists Hooks
 * @see US-39.3
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryListsService } from '../services/inventory-lists.service';
import type { CreateInventoryListDto, UpdateInventoryListDto, UpdateInventoryListItemsDto } from '@bstock/shared';

const QUERY_KEY = 'inventory-lists';

export function useInventoryLists() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => inventoryListsService.getAll(),
  });
}

export function useActiveInventoryLists() {
  return useQuery({
    queryKey: [QUERY_KEY, 'active'],
    queryFn: () => inventoryListsService.getActive(),
  });
}

export function useInventoryList(id: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => inventoryListsService.getById(id!),
    enabled: !!id,
  });
}

export function useInventoryListMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  const create = useMutation({
    mutationFn: (data: CreateInventoryListDto) => inventoryListsService.create(data),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryListDto }) =>
      inventoryListsService.update(id, data),
    onSuccess: invalidate,
  });

  const replaceItems = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryListItemsDto }) =>
      inventoryListsService.replaceItems(id, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => inventoryListsService.delete(id),
    onSuccess: invalidate,
  });

  return { create, update, replaceItems, remove };
}
